import { createActions, createReducer } from 'reduxsauce';

// ---------------------------------------------------------------------------
// INITIAL STATE
// ---------------------------------------------------------------------------
export const INITIAL_STATE = {
  // coupon daily stats — keyed by couponId so multiple coupons can load
  // in parallel without clobbering each other
  dailyStats: {
    byCoupon: {},
  },

  // position-bucket breakdown — same per-coupon keying
  positionBuckets: {
    byCoupon: {},
  },

  // screen breakdown — same per-coupon keying as positionBuckets
  screenStats: {
    byCoupon: {},
  },

  // windowed ranking — single shared result (global list)
  ranking: {
    fetching: false,
    fetched: false,
    data: [],
    windowDays: null,
    error: null,
  },

  // aggregated per-business stats — keyed by `${businessId}:${slot}`
  // ('all' for the admin cross-business view; slot defaults to 'default'
  // when the caller doesn't specify one). The slot is what lets two
  // independent panels (e.g. Dashboard's two RedemptionsChart instances)
  // fetch the SAME business's stats concurrently without clobbering each
  // other's slice — see saga.js/getBusinessStats for where slot comes from.
  businessStats: {
    byBusiness: {},
  },

  // per-user daily stats from user_daily_stats table
  // keyed by userId so an admin viewing multiple users doesn't clobber
  userDailyStats: {
    byUser: {},
  },

  // guest-scoped slice of the SAME user_daily_stats table (rows with a
  // negative guest_pseudo_user_id()). Kept as its own top-level slice,
  // not merged into userDailyStats.byUser, so a signed-in fetch and a
  // guest fetch under the same key ('all') can't overwrite each other.
  guestDailyStats: {
    byUser: {},
  },

  // per-user per-coupon daily stats — keyed by userId
  userCouponStats: {
    byUser: {},
  },

  // raw granular events for the current user (last 14 days)
  userRawEvents: {
    fetching: false,
    fetched: false,
    data: [],
    error: null,
  },

  // event ingest — fire-and-forget; track in-flight for backpressure only
  events: {
    sending: false,
    error: null,
    lastAccepted: 0,
  },
};

// ---------------------------------------------------------------------------
// ACTIONS
// ---------------------------------------------------------------------------
const { Types, Creators } = createActions(
  {
    // --- coupon daily stats ---
    getDailyStats:        ['params'],
    getDailyStatsRequest: ['couponId'],
    getDailyStatsSuccess: ['couponId', 'data'],
    getDailyStatsFailure: ['couponId', 'error'],

    // --- position buckets ---
    getPositionBuckets:        ['params'],
    getPositionBucketsRequest: ['couponId'],
    getPositionBucketsSuccess: ['couponId', 'data'],
    getPositionBucketsFailure: ['couponId', 'error'],

    // --- screen stats ---
    getScreenStats:        ['params'],
    getScreenStatsRequest: ['couponId'],
    getScreenStatsSuccess: ['couponId', 'data'],
    getScreenStatsFailure: ['couponId', 'error'],

    // --- windowed ranking ---
    getRanking:        ['params'],
    getRankingRequest: [],
    getRankingSuccess: ['data', 'windowDays'],
    getRankingFailure: ['error'],

    // --- business stats ---
    getBusinessStats:        ['params'],
    getBusinessStatsRequest: ['businessId', 'slot'],
    getBusinessStatsSuccess: ['businessId', 'slot', 'data'],
    getBusinessStatsFailure: ['businessId', 'slot', 'error'],

    // --- user daily stats ---
    getUserDailyStats:        ['params'],
    getUserDailyStatsRequest: ['userId'],
    getUserDailyStatsSuccess: ['userId', 'data'],
    getUserDailyStatsFailure: ['userId', 'error'],

    // --- guest daily stats (same table, guest-scoped) ---
    getGuestDailyStats:        ['params'],
    getGuestDailyStatsRequest: ['userId'],
    getGuestDailyStatsSuccess: ['userId', 'data'],
    getGuestDailyStatsFailure: ['userId', 'error'],

    // --- user coupon daily stats ---
    getUserCouponStats:        ['params'],
    getUserCouponStatsRequest: ['userId'],
    getUserCouponStatsSuccess: ['userId', 'data'],
    getUserCouponStatsFailure: ['userId', 'error'],

    // --- user raw events (granular, 14-day window) ---
    getUserRawEvents:        ['params'],
    getUserRawEventsRequest: [],
    getUserRawEventsSuccess: ['data'],
    getUserRawEventsFailure: ['error'],

    // --- event ingest (batch write) ---
    sendEvents:        ['events', 'resolve'],
    sendEventsRequest: [],
    sendEventsSuccess: ['accepted'],
    sendEventsFailure: ['error'],
  },
  { prefix: 'cb/stats/' },
);

export { Types as types, Creators as actions };

// ---------------------------------------------------------------------------
// REDUCER HELPERS
// ---------------------------------------------------------------------------
const couponSlice = (state, key, couponId) => ({
  ...state[key].byCoupon[couponId],
});

const withCouponSlice = (state, key, couponId, patch) => ({
  ...state,
  [key]: {
    ...state[key],
    byCoupon: {
      ...state[key].byCoupon,
      [couponId]: {
        ...couponSlice(state, key, couponId),
        ...patch,
      },
    },
  },
});

// slot defaults to 'default' so any caller that doesn't pass one (or old
// persisted state from before this change) still resolves to a stable key.
const businessKey = (businessId, slot = 'default') => `${businessId}:${slot}`;

const businessSlice = (state, businessId, slot) => ({
  ...state.businessStats.byBusiness[businessKey(businessId, slot)],
});

const withBusinessSlice = (state, businessId, slot, patch) => ({
  ...state,
  businessStats: {
    ...state.businessStats,
    byBusiness: {
      ...state.businessStats.byBusiness,
      [businessKey(businessId, slot)]: {
        ...businessSlice(state, businessId, slot),
        ...patch,
      },
    },
  },
});

const userSlice = (state, userId) => ({
  ...state.userDailyStats.byUser[userId],
});

const withUserSlice = (state, userId, patch) => ({
  ...state,
  userDailyStats: {
    ...state.userDailyStats,
    byUser: {
      ...state.userDailyStats.byUser,
      [userId]: {
        ...userSlice(state, userId),
        ...patch,
      },
    },
  },
});

// ---------------------------------------------------------------------------
// HANDLERS
// ---------------------------------------------------------------------------
export const HANDLERS = {
  // --- coupon daily stats ---
  [Types.GET_DAILY_STATS_REQUEST]: (state, { couponId }) =>
    withCouponSlice(state, 'dailyStats', couponId, {
      fetching: true, fetched: false, data: null, error: null,
    }),
  [Types.GET_DAILY_STATS_SUCCESS]: (state, { couponId, data }) =>
    withCouponSlice(state, 'dailyStats', couponId, {
      fetching: false, fetched: true, data, error: null,
    }),
  [Types.GET_DAILY_STATS_FAILURE]: (state, { couponId, error }) =>
    withCouponSlice(state, 'dailyStats', couponId, {
      fetching: false, fetched: false, data: null, error,
    }),

  // --- position buckets ---
  [Types.GET_POSITION_BUCKETS_REQUEST]: (state, { couponId }) =>
    withCouponSlice(state, 'positionBuckets', couponId, {
      fetching: true, fetched: false, data: null, error: null,
    }),
  [Types.GET_POSITION_BUCKETS_SUCCESS]: (state, { couponId, data }) =>
    withCouponSlice(state, 'positionBuckets', couponId, {
      fetching: false, fetched: true, data, error: null,
    }),
  [Types.GET_POSITION_BUCKETS_FAILURE]: (state, { couponId, error }) =>
    withCouponSlice(state, 'positionBuckets', couponId, {
      fetching: false, fetched: false, data: null, error,
    }),

  // --- screen stats ---
  [Types.GET_SCREEN_STATS_REQUEST]: (state, { couponId }) =>
    withCouponSlice(state, 'screenStats', couponId, {
      fetching: true, fetched: false, data: null, error: null,
    }),
  [Types.GET_SCREEN_STATS_SUCCESS]: (state, { couponId, data }) =>
    withCouponSlice(state, 'screenStats', couponId, {
      fetching: false, fetched: true, data, error: null,
    }),
  [Types.GET_SCREEN_STATS_FAILURE]: (state, { couponId, error }) =>
    withCouponSlice(state, 'screenStats', couponId, {
      fetching: false, fetched: false, data: null, error,
    }),

  // --- ranking ---
  [Types.GET_RANKING_REQUEST]: state => ({
    ...state,
    ranking: { ...state.ranking, fetching: true, error: null },
  }),
  [Types.GET_RANKING_SUCCESS]: (state, { data, windowDays }) => ({
    ...state,
    ranking: { fetching: false, fetched: true, data, windowDays, error: null },
  }),
  [Types.GET_RANKING_FAILURE]: (state, { error }) => ({
    ...state,
    ranking: { ...state.ranking, fetching: false, error },
  }),

  // --- business stats ---
  [Types.GET_BUSINESS_STATS_REQUEST]: (state, { businessId, slot }) =>
    withBusinessSlice(state, businessId, slot, {
      fetching: true, fetched: false, data: null, error: null,
    }),
  [Types.GET_BUSINESS_STATS_SUCCESS]: (state, { businessId, slot, data }) =>
    withBusinessSlice(state, businessId, slot, {
      fetching: false, fetched: true, data, error: null,
    }),
  [Types.GET_BUSINESS_STATS_FAILURE]: (state, { businessId, slot, error }) =>
    withBusinessSlice(state, businessId, slot, {
      fetching: false, fetched: false, data: null, error,
    }),

  // --- user daily stats ---
  [Types.GET_USER_DAILY_STATS_REQUEST]: (state, { userId }) =>
    withUserSlice(state, userId, {
      fetching: true, fetched: false, data: null, error: null,
    }),
  [Types.GET_USER_DAILY_STATS_SUCCESS]: (state, { userId, data }) =>
    withUserSlice(state, userId, {
      fetching: false, fetched: true, data, error: null,
    }),
  [Types.GET_USER_DAILY_STATS_FAILURE]: (state, { userId, error }) =>
    withUserSlice(state, userId, {
      fetching: false, fetched: false, data: null, error,
    }),

  // --- guest daily stats ---
  [Types.GET_GUEST_DAILY_STATS_REQUEST]: (state, { userId }) => ({
    ...state,
    guestDailyStats: {
      ...state.guestDailyStats,
      byUser: {
        ...state.guestDailyStats.byUser,
        [userId]: { fetching: true, fetched: false, data: null, error: null },
      },
    },
  }),
  [Types.GET_GUEST_DAILY_STATS_SUCCESS]: (state, { userId, data }) => ({
    ...state,
    guestDailyStats: {
      ...state.guestDailyStats,
      byUser: {
        ...state.guestDailyStats.byUser,
        [userId]: { fetching: false, fetched: true, data, error: null },
      },
    },
  }),
  [Types.GET_GUEST_DAILY_STATS_FAILURE]: (state, { userId, error }) => ({
    ...state,
    guestDailyStats: {
      ...state.guestDailyStats,
      byUser: {
        ...state.guestDailyStats.byUser,
        [userId]: { fetching: false, fetched: false, data: null, error },
      },
    },
  }),

  // --- user coupon stats ---
  [Types.GET_USER_COUPON_STATS_REQUEST]: (state, { userId }) => ({
    ...state,
    userCouponStats: {
      ...state.userCouponStats,
      byUser: {
        ...state.userCouponStats.byUser,
        [userId]: { fetching: true, fetched: false, data: null, error: null },
      },
    },
  }),
  [Types.GET_USER_COUPON_STATS_SUCCESS]: (state, { userId, data }) => ({
    ...state,
    userCouponStats: {
      ...state.userCouponStats,
      byUser: {
        ...state.userCouponStats.byUser,
        [userId]: { fetching: false, fetched: true, data, error: null },
      },
    },
  }),
  [Types.GET_USER_COUPON_STATS_FAILURE]: (state, { userId, error }) => ({
    ...state,
    userCouponStats: {
      ...state.userCouponStats,
      byUser: {
        ...state.userCouponStats.byUser,
        [userId]: { fetching: false, fetched: false, data: null, error },
      },
    },
  }),

  // --- user raw events ---
  [Types.GET_USER_RAW_EVENTS_REQUEST]: state => ({
    ...state,
    userRawEvents: { ...state.userRawEvents, fetching: true, error: null },
  }),
  [Types.GET_USER_RAW_EVENTS_SUCCESS]: (state, { data }) => ({
    ...state,
    userRawEvents: { fetching: false, fetched: true, data, error: null },
  }),
  [Types.GET_USER_RAW_EVENTS_FAILURE]: (state, { error }) => ({
    ...state,
    userRawEvents: { ...state.userRawEvents, fetching: false, error },
  }),

  // --- event ingest ---
  [Types.SEND_EVENTS_REQUEST]: state => ({
    ...state,
    events: { ...state.events, sending: true, error: null },
  }),
  [Types.SEND_EVENTS_SUCCESS]: (state, { accepted }) => ({
    ...state,
    events: { sending: false, error: null, lastAccepted: accepted },
  }),
  [Types.SEND_EVENTS_FAILURE]: (state, { error }) => ({
    ...state,
    events: { ...state.events, sending: false, error },
  }),
};

export default createReducer(INITIAL_STATE, HANDLERS);